import { cn } from '@/lib/utils'

import { PROVIDER_ICON_COMPONENTS } from './icons'

/**
 * Ícones dos provedores de autenticação, em SVG.
 *
 * Vinham de um sprite raster (`public/icons/social-sheet.png`, folha 1× de
 * 111×37 recortada por `background-position`). A folha resolvia o serrilhado em
 * 1× e não tinha resposta para densidade: em tela de 2× ou 3× — a maioria dos
 * celulares, que é onde este app é consumido — o navegador ampliava e a borda
 * perdia definição. SVG resolve de vez, e o pipeline `pnpm icons:build` já
 * existia no projeto.
 *
 * Os componentes são gerados de `public/icons/provider-<name>.svg`. O slug é
 * prefixado porque `SocialIcon` já é o nome do ícone ilustrado do módulo
 * "social" em `shared/ui/EvnttzIcons` — mesmo nome, outro componente.
 *
 * A prop `zoom` da versão anterior não existe mais: ela só servia para recortar
 * a margem de uma célula de folha raster, e não há folha.
 */

const COMPONENTES = {
  google: PROVIDER_ICON_COMPONENTS['provider-google'],
  apple: PROVIDER_ICON_COMPONENTS['provider-apple'],
  email: PROVIDER_ICON_COMPONENTS['provider-email']
} as const

/**
 * Respiro que a folha embutia e o SVG não tem.
 *
 * Na folha, cada desenho ocupava cerca de **metade** da célula de 37px — o
 * Google media 18×18, a Apple 17×19, o e-mail 18×14 — e o resto era
 * transparência. A caixa era 36px, mas o que se via tinha ~18px.
 *
 * Os SVGs são as marcas oficiais, desenhadas até a borda do próprio viewBox.
 * Renderizados na mesma caixa de 36px, saem com quase o dobro do tamanho de
 * antes. O fator devolve a proporção medida na folha, e fica aqui — e não no
 * arquivo — porque mexer no viewBox de um asset de marca é alterar a marca.
 *
 * Cada fator é calibrado pelo preenchimento do próprio desenho no seu viewBox,
 * que não é igual entre os três: o "G" chega perto da borda, a maçã é mais
 * estreita, e o envelope é largo e baixo.
 */
const RESPIRO: Record<SocialIconName, number> = {
  google: 0.56,
  apple: 0.67,
  email: 0.58
}

/**
 * Tinta por provedor.
 *
 * Google e Apple são marcas de terceiro: as cores são parte do ativo e não
 * respondem a nada — pintar o "G" com a cor do evento seria descaracterizar
 * uma marca que não é nossa. O envelope é ícone do produto, então segue a
 * marca do evento.
 *
 * O SVG fonte usa `currentColor` justamente para essa decisão morar aqui, na
 * camada que conhece o contexto, e não cravada no ativo.
 */
const TINTA: Partial<Record<SocialIconName, string>> = {
  email: 'text-[var(--color-brand)]'
}

export type SocialIconName = keyof typeof COMPONENTES

type SocialIconProps = {
  name: SocialIconName
  /** Lado da caixa, em pixels — o mesmo espaço que a célula da folha ocupava. */
  size?: number
  className?: string
}

export function SocialIcon({ name, size = 36, className }: SocialIconProps) {
  const Icone = COMPONENTES[name]
  // Arredondado: meio pixel de caixa deixa a borda do desenho fora da grade e
  // reintroduz, em vetor, a falta de nitidez que tirar o raster resolveu.
  const lado = Math.round(size * RESPIRO[name])

  return (
    // A caixa externa preserva o espaço que a folha ocupava, para as três
    // linhas manterem o mesmo eixo de leitura. O desenho é centrado nela.
    <span
      // Decorativo: o rótulo do botão já nomeia o provedor. Anunciá-lo de novo
      // faria o leitor de tela repetir "Google" duas vezes na mesma linha.
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center',
        TINTA[name],
        className
      )}
      style={{ width: size, height: size }}
    >
      <Icone focusable="false" width={lado} height={lado} />
    </span>
  )
}
